import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "MyEcoTour";
const DEFAULT_DESCRIPTION =
    "Bespoke eco-luxury travel concierge — hand-drafted, carbon-neutral journeys across India, Asia, Africa, Europe and the Americas. Honeymoons · Wellness · Wildlife.";
const DEFAULT_IMAGE = "https://myecotour.in/api/assets/img/antarctica-penguins.jpg";
const PROD_ORIGIN = "https://myecotour.in";

// Per Bing/Google guidance:
//  - <title>:  ≤ 60 chars renders fully in SERP without truncation
//  - <meta description>: ≤ 155 chars renders fully in SERP without truncation
// We hard-cap both in `composeTitle` and `composeDescription` so admin-authored
// titles or LLM-generated story hooks can't silently push us into Bing's
// "Title too long" / "Description too long" report categories.
const MAX_TITLE_CHARS = 60;
const MAX_DESCRIPTION_CHARS = 155;

function composeTitle(rawTitle) {
    if (!rawTitle) return `${SITE_NAME} — Eco-Luxury Travel & Honeymoon Concierge`;
    const withBrand = `${rawTitle} · ${SITE_NAME}`;
    if (withBrand.length <= MAX_TITLE_CHARS) return withBrand;
    // Title alone fits — drop the brand suffix to stay under cap.
    if (rawTitle.length <= MAX_TITLE_CHARS) return rawTitle;
    // Page title itself is too long. Trim to fit room for "… · MyEcoTour".
    const tail = ` · ${SITE_NAME}`;
    const room = MAX_TITLE_CHARS - tail.length - 1; // -1 for the ellipsis
    return `${rawTitle.slice(0, room).trimEnd()}… · ${SITE_NAME}`;
}

function composeDescription(raw) {
    const text = (raw || DEFAULT_DESCRIPTION).replace(/\s+/g, " ").trim();
    if (text.length <= MAX_DESCRIPTION_CHARS) return text;
    return `${text.slice(0, MAX_DESCRIPTION_CHARS - 1).trimEnd()}…`;
}

function siteOrigin() {
    if (typeof window === "undefined") return PROD_ORIGIN;
    // Always emit canonical against production host so preview deploys don't
    // dilute SEO equity from search-engine perspective.
    return PROD_ORIGIN;
}

function currentPath() {
    if (typeof window === "undefined") return "";
    return window.location.pathname + window.location.search;
}

function absolutise(img) {
    if (!img) return DEFAULT_IMAGE;
    return img.startsWith("/") ? `${PROD_ORIGIN}${img}` : img;
}

export default function Seo({
    title,
    description = DEFAULT_DESCRIPTION,
    image = DEFAULT_IMAGE,
    portraitImage,
    type = "website",
    canonicalPath,
    keywords,
    noindex = false,
    jsonLd,
    hreflangs,
}) {
    const fullTitle = composeTitle(title);
    const url = `${siteOrigin()}${canonicalPath || currentPath()}`;
    const cleanedDescription = composeDescription(description);
    const absImage = absolutise(image);
    const absPortrait = portraitImage ? absolutise(portraitImage) : null;
    // Remove the static default SEO tags baked into index.html (marked with
    // data-static-seo). Social crawlers pick the FIRST og:image in <head>, so
    // leaving the static defaults in place would make every shared link
    // preview with the site-wide default instead of this page's own image.
    // React 19 hoisting appends Helmet tags — it never replaces static ones.
    useEffect(() => {
        document.head
            .querySelectorAll("[data-static-seo]")
            .forEach((el) => el.remove());
    }, []);
    // hreflang alternates are DOM-managed (not Helmet) — Helmet dedupes link
    // tags sharing rel+href, which collapses a single-URL hreflang cluster.
    useEffect(() => {
        document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
        if (hreflangs && hreflangs.length) {
            [...hreflangs, "x-default"].forEach((lang) => {
                const el = document.createElement("link");
                el.rel = "alternate";
                el.setAttribute("hreflang", lang);
                el.href = url;
                document.head.appendChild(el);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(hreflangs || []).join(","), url]);
    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={cleanedDescription} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={url} />
            {noindex
                ? <meta name="robots" content="noindex, nofollow" />
                : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}

            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:locale" content="en_IN" />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={cleanedDescription} />
            <meta property="og:image" content={absImage} />
            <meta property="og:image:alt" content={title || SITE_NAME} />
            {absPortrait && (
                <meta property="og:image" content={absPortrait} />
            )}
            {absPortrait && (
                <meta property="og:image:width" content="1000" />
            )}
            {absPortrait && (
                <meta property="og:image:height" content="1500" />
            )}
            <meta property="og:url" content={url} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@myecotour" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={cleanedDescription} />
            <meta name="twitter:image" content={absImage} />

            {jsonLd && (
                <script type="application/ld+json">
                    {/* \u003c-escape prevents </script> breakout from content-derived strings */}
                    {JSON.stringify(jsonLd).replace(/</g, "\\u003c")}
                </script>
            )}
        </Helmet>
    );
}
